import * as d3 from "d3";
import { options } from "less";
import { DataStore } from "@/datastore";
const Enum = require("@/utils/enums").default;

const AXIS_TICK_FONT_SIZE = 12;
const AXIS_X_TICK_FONT_SIZE = 14;
const AXIS_UNIT_FONT_SIZE = "16px";
const AXIS_TITLE_FONT_SIZE = "15px";
const Y_AXIS_PAD_FRACTION = 0.04;

const TICK_DAY_MS = 86400000;
const DATE_TICK_STEPS = [
  { ms: TICK_DAY_MS, interval: d3.timeDay },
  { ms: 2 * TICK_DAY_MS, interval: d3.timeDay.every(2) },
  { ms: 5 * TICK_DAY_MS, interval: d3.timeDay.filter(d => d.getDate() === 1 || (d.getDate() % 5 === 0 && d.getDate() < 30)) },
  { ms: 10 * TICK_DAY_MS, interval: d3.timeDay.filter(d => [1, 10, 20].includes(d.getDate())) },
  { ms: 15 * TICK_DAY_MS, interval: d3.timeDay.filter(d => d.getDate() === 1 || d.getDate() === 15) },
  { ms: 30 * TICK_DAY_MS, interval: d3.timeMonth },
  { ms: 61 * TICK_DAY_MS, interval: d3.timeMonth.every(2) },
  { ms: 91 * TICK_DAY_MS, interval: d3.timeMonth.every(3) },
  { ms: 182 * TICK_DAY_MS, interval: d3.timeMonth.every(6) },
  { ms: 365 * TICK_DAY_MS, interval: d3.timeYear },
  { ms: 2 * 365 * TICK_DAY_MS, interval: d3.timeYear.every(2) },
  { ms: 5 * 365 * TICK_DAY_MS, interval: d3.timeYear.every(5) },
  { ms: 10 * 365 * TICK_DAY_MS, interval: d3.timeYear.every(10) },
];

export function date_axis_ticks(from, to, max_count) {
  const span = +to - +from;
  const step = DATE_TICK_STEPS.find(s => span / s.ms <= max_count * 1.2) ??
    DATE_TICK_STEPS[DATE_TICK_STEPS.length - 1];
  return { ticks: step.interval.range(new Date(from), new Date(+to + 1)), step_ms: step.ms };
}

const toNum = v => { if (v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const isFiniteNum = v => Number.isFinite(Number(v));
const getT = d => (d && (d.minDate ?? d.date)?.getTime()) ?? -Infinity;

// Direct getBBox wrapper — kept for readability. The earlier caching version
// was unsafe: bbox.x/y depend on the text element's x/y attributes (and
// text-anchor), which change between renders when axes shift, so reusing
// cached x/y put rect backgrounds in the wrong place and broke hover regions.
// Width/height only depend on font + text content but the savings from caching
// just those two are marginal — keeping it simple is worth the small cost.
function measureText(textNode) {
  return textNode.getBBox();
}

function fit_x_axis_end_labels(axis_x, x, start, end, svg_width) {
  const GAP = 6;
  const width = node => {
    let w = 0;
    try { w = measureText(node).width; } catch (e) { /* not laid out */ }
    return w > 0 ? w : node.textContent.length * AXIS_X_TICK_FONT_SIZE * 0.6;
  };
  let left = -Infinity;
  let right = Infinity;
  const inner = [];
  axis_x.selectAll(".tick text").attr("dx", null).style("display", null).each(function (d) {
    const cx = x(d);
    const w = width(this);
    if (+d !== +start && +d !== +end) {
      inner.push({ node: this, cx, w });
      return;
    }
    const shift = Math.max(0, w / 2 - cx) + Math.min(0, svg_width - (cx + w / 2));
    if (shift) this.setAttribute("dx", shift);
    if (+d === +start) left = cx + shift + w / 2 + GAP;
    else right = cx + shift - w / 2 - GAP;
  });
  for (const t of inner) {
    if (t.cx - t.w / 2 < left || t.cx + t.w / 2 > right) t.node.style.display = "none";
  }
}

// Per-series value extent cache. Y-axis autoscale needs min/max across the
// whole series — scanning every data point for every autoscaled axis on every
// scroll tick is the dominant remaining cost when many channels × many points
// are loaded. We cache by array reference + length: graphbuffer mutates data
// arrays in place but always changes length on new blocks / null inserts, so
// length is a sufficient fingerprint to detect "data changed, recompute."
const _extentCache = new WeakMap();
function getDataExtent(data) {
  const cached = _extentCache.get(data);
  if (cached && cached.length === data.length) return cached.extent;
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    if (!d) continue;
    if ("value" in d) {
      const v = Number(d.value);
      if (Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    } else {
      const vmin = Number(d.minVal);
      const vmax = Number(d.maxVal);
      if (Number.isFinite(vmin) && vmin < min) min = vmin;
      if (Number.isFinite(vmax) && vmax > max) max = vmax;
    }
  }
  const extent = (min === Infinity || max === -Infinity) ? null : [min, max];
  _extentCache.set(data, { length: data.length, extent });
  return extent;
}

function bisectLeft(arr, val) {
  let lo = 0, hi = arr.length;
  while (lo < hi) { const mid = (lo + hi) >>> 1; if (getT(arr[mid]) < val) lo = mid + 1; else hi = mid; }
  return lo;
}
function bisectRight(arr, val) {
  let lo = 0, hi = arr.length;
  while (lo < hi) { const mid = (lo + hi) >>> 1; if (getT(arr[mid]) <= val) lo = mid + 1; else hi = mid; }
  return lo;
}
function finiteExtent(arr) {
  const vals = arr.map(Number).filter(Number.isFinite);
  if (!vals.length) return [0, 1];
  let min = Math.min(...vals), max = Math.max(...vals);
  if (min === max) { const pad = Math.max(1e-6, Math.abs(min || 1) * 0.01); min -= pad; max += pad; }
  return [min, max];
}

let TG_SERIES_CONTAINER = null;
let axesDistance = 60;
let yLabelDistance = 48;
let xLegendDistance = 30;
let xLegendOffset = 0;
let paddingSplit=50;
let maxAxis = 20;

const HALF_HOUR = 1 / 24 / 2;

export function get_min_value(series) {
  let candidates = [];
  for (let s of series) {
    if (s.data[0].hasOwnProperty("value")) {
      candidates.push(
        s.data.reduce((lowest, samp) => {
          return samp.value < lowest ? samp.value : lowest;
        }, Infinity)
      );
    } else {
      candidates.push(
        s.data.reduce((lowest, samp) => {
          return samp.minVal < lowest ? samp.minVal : lowest;
        }, Infinity)
      );
    }
  }

  return Math.min.apply(null, candidates);
}

function get_max_value(series) {
  let candidates = [];
  for (let s of series) {
    if (s.data[0].hasOwnProperty("value")) {
      candidates.push(
        s.data.reduce((highest, samp) => {
          return samp.value > highest ? samp.value : highest;
        }, 0)
      );
    } else {
      candidates.push(
        s.data.reduce((highest, samp) => {
          return samp.maxVal > highest ? samp.maxVal : highest;
        }, 0)
      );
    }
  }
  return Math.max.apply(null, candidates);
}

export function init_graph(canvas_id, height, width) {
  let canvas = d3.select("#" + canvas_id);
  canvas.select(".tg_focus_circle").style("display", "none");
}

function clear_graph(canvas, activeSeriesIds = null) {
    // Series container: don't wipe blindly. The per-series renderer can reuse
    // existing groups/paths in place via D3's select-or-create pattern, which
    // is dramatically cheaper than tear-down + rebuild every frame. We only
    // remove groups for series that are no longer present in the data.
    TG_SERIES_CONTAINER = canvas.select(".tg_series_container");
    if (!TG_SERIES_CONTAINER.empty()) {
      if (activeSeriesIds) {
        TG_SERIES_CONTAINER.selectAll("g").each(function () {
          const elementId = this.id;
          if (!elementId) return;
          const match = elementId.match(/^series_(.+)$/);
          if (match && !activeSeriesIds.has(match[1])) this.remove();
        });
      } else {
        // Caller didn't tell us what's active — fall back to the old behaviour.
        TG_SERIES_CONTAINER.selectAll('*').remove();
      }
    }

    canvas.selectAll(".clipPath").remove();
    canvas.selectAll(".tg_focus_circles").style("display", "none");
    canvas.selectAll("circle").remove();
    canvas.selectAll(".legend_label").remove();
    canvas.selectAll(".barrect").remove();

    // Remove all Y-axis elements by ID pattern to prevent stale axes from persisting
    // This ensures clean slate even if axesData.array doesn't match DOM state
    canvas.selectAll("[id^='tg_axis_']").remove();
    canvas.selectAll("[id^='tg_y_axis_value_label_']").remove();
    canvas.selectAll("[id^='tg_y_axis_label_']").remove();
    canvas.selectAll("[id^='tg_axis_grid_']").remove();
    canvas.selectAll("[id^='tg_axis_zero_']").remove();
    canvas.selectAll("[id^='tg_axis_value_Line_']").remove();
}

export function stacked_graph_positions(visible_series) {
  const used = new Set();
  for (const s of visible_series) {
    if (s.yPos == Enum.graph_pos.graph_pos_top) used.add(Enum.graph_pos.graph_pos_top);
    else if (s.yPos == Enum.graph_pos.graph_pos_high) used.add(Enum.graph_pos.graph_pos_high);
    else used.add(Enum.graph_pos.graph_pos_bottom);
  }
  if (used.size == 0) used.add(Enum.graph_pos.graph_pos_bottom);
  return [...used].sort((a, b) => a - b);
}


export function render_graph(
  options,
  axesData,
  series,
  hidden_series,
  alarmLineHigh,
  alarmLineLow,
  showHoldValues,
  unique_identifier
) 
{
  // Work on a local copy — prevents caller's object from accumulating margin mutations on each render
  options = { ...options };

  let canvas = d3.select("#" + options.canvasId);

  // Define extra margins
  let extraMarginX = 35; // Adjust as needed
  let extraMarginY = 0; // Adjust as needed

  // Update the options to include extra margins
  options.width += extraMarginX * 2;
  options.height += extraMarginY * 2;

  // Increase padding values
  options.paddingXLeft += extraMarginX;
  options.paddingXRight += extraMarginX;
  options.paddingY += extraMarginY;

  // Update the SVG dimensions
  canvas
    .attr("width", options.width)
    .attr("height", options.height)
    .attr("viewBox", `0 0 ${options.width} ${options.height}`);

  // Pass currently-rendered series IDs so clear_graph can preserve their DOM
  // for in-place reuse (only stale series_groups get removed).
  const activeSeriesIds = new Set((series || []).map(s => String(s.id)));
  clear_graph(canvas, activeSeriesIds);

  // Pre-clear paths for currently-hidden series. The per-axis rendering loop
  // below only visits the yPos values from stacked_graph_positions(visible_series).
  // When the user hides every channel on a yPos (say all Voltage channels),
  // that yPos gets no row and its axis is skipped entirely — so draw_series never runs for those
  // series and their hidden-cleanup never fires, leaving stale paths visible
  // from when they were rendered previously. This loop catches that case.
  if (TG_SERIES_CONTAINER && !TG_SERIES_CONTAINER.empty() && hidden_series && hidden_series.length) {
    const hiddenSet = new Set(hidden_series.map(id => String(id)));
    for (const s of series || []) {
      if (!hiddenSet.has(String(s.id))) continue;
      const sg = TG_SERIES_CONTAINER.select("#series_" + s.id);
      if (sg.empty()) continue;
      sg.selectAll("path").remove();
      sg.selectAll("line.tg_minmax_bar").remove();
    }
  }

  window.points_drawn = 0;

  let visible_series = series.filter(s => !hidden_series.includes(s.id));
  const visible_ids = new Set(visible_series.map(s => s.id));
  const is_visible = d => visible_ids.has(d.id);

  // Stacked graphs with same X-axis: row r (0 = bottom) shows yPos graph_positions[r]
  const graph_positions = stacked_graph_positions(visible_series);
  let graph_N = graph_positions.length;

//  console.log("graph_N=", graph_N);

  let displayed_interval_end = options.endDate;
  let displayed_interval_start = options.startDate;
  let time_interval = options.endDate - options.startDate;

  if (!options.disable_zoom) {
    displayed_interval_start =
      options.position -
      (time_interval / 2) * options.interval_scaling_factor +
      options.offset_ms;
    displayed_interval_end =
      options.position +
      (time_interval / 2) * options.interval_scaling_factor +
      options.offset_ms;
  }

  // Inset the plot just enough to lift the first/last sample off the y-axis
  // strokes (≈1px) — small so the axes still read as sitting at the data edge
  // (origo). The x-axis ticks/grid use the same scale, so they stay aligned.
  const X_EDGE_INSET = 1.5;
  const x = d3
    .scaleTime()
    .domain([displayed_interval_start, displayed_interval_end])
    .range([options.paddingXLeft + X_EDGE_INSET, options.width - options.paddingXRight - X_EDGE_INSET]);

  axesData.xRange = x.range();

  const xCoord = d => {
    return d.hasOwnProperty("date") ? x(d.date) : x(d.minDate);
  };

  const spanMs = displayed_interval_end - displayed_interval_start;
  const DAY_MS = 86400000;
  const HALF_HOUR_MS = 30 * 60000;
  const showSeconds = spanMs < HALF_HOUR_MS;
  const pdt = (t, opts) => window.app.langutil.print_date_time(t, opts);
  const dateOnly = (t, show_year = true, show_month = true) =>
    pdt(t, { show_date: true, show_year, show_month, show_time: false });
  const timeOnly = t => pdt(t, { show_date: false, show_time: true, show_seconds: showSeconds });
  const dateAndTime = t => pdt(t, { show_date: true, show_time: true, show_seconds: showSeconds });

  const xTickCount = (options.width - options.paddingXLeft - options.paddingXRight) / 140;
  let xTickValues, xStepMs;
  if (spanMs >= 8 * DAY_MS) {
    ({ ticks: xTickValues, step_ms: xStepMs } =
      date_axis_ticks(displayed_interval_start, displayed_interval_end, xTickCount));
  } else {
    xTickValues = x.ticks(xTickCount);
    xStepMs = xTickValues.length > 1 ? xTickValues[1] - xTickValues[0] : spanMs;
  }

  const boldTicks = new Set();
  const markBold = (value, { year, month, date }) => {
    if ((year && xStepMs < 365 * DAY_MS) || (month && xStepMs < 28 * DAY_MS) || (date && xStepMs < DAY_MS)) {
      boldTicks.add(+value);
    }
  };

  const startTick = new Date(displayed_interval_start);
  const endTick = new Date(displayed_interval_end);
  const axisTickValues = [
    startTick,
    ...xTickValues.filter(t => +t > +startTick && +t < +endTick),
    endTick,
  ];

  // d3 formats the ticks in order, so the previous tick is known.
  let previousTick = null;
  let format = (value, i) => {
    const d = (value instanceof Date) ? value : new Date(value);
    const yearChanged = i === 0 || d.getFullYear() !== previousTick.getFullYear();
    const monthChanged = yearChanged || d.getMonth() !== previousTick.getMonth();
    previousTick = d;
    const atMidnight = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
    if (+d === +startTick || +d === +endTick) { // ends: full context
      boldTicks.add(+value);
      return spanMs >= 8 * DAY_MS || atMidnight ? dateOnly(value) : dateAndTime(value);
    }
    if (spanMs >= 8 * DAY_MS) {
      markBold(value, { year: yearChanged, month: monthChanged });
      return dateOnly(value, yearChanged, monthChanged);
    }
    if (atMidnight) {                           // day boundary: just the date
      markBold(value, { year: yearChanged, month: monthChanged, date: true });
      return dateOnly(value, yearChanged);
    }
    return timeOnly(value);                     // within a day: time only
  };
  // The hover header always wants the full timestamp, independent of tick context.
  const formatFull = t => dateAndTime(t);

  // Axis
  let bottomAxis = d3
    .axisBottom(x)
    .tickFormat(format)
    .tickValues(axisTickValues);

    // Adjust positions that depend on options.width and options.height
    // For example, when positioning the bottom axis:
    let axis_x = canvas
      .select(".tg_axis")
      .attr(
        "transform",
        `translate(0, ${options.height - options.paddingY})`
      )
      .call(bottomAxis)
      .attr("font-size", AXIS_X_TICK_FONT_SIZE);
    axis_x.selectAll(".tick text")
      .attr("font-weight", d => (boldTicks.has(+d) ? "bold" : null));
    fit_x_axis_end_labels(axis_x, x, startTick, endTick, options.width);

    // Adjust description position
    let descriptionX = options.width - options.paddingXRight - xLegendOffset - extraMarginX;
    let descriptionY = options.height - 5 - extraMarginY;
    // Drag directly on the X-axis to pan time (mousedown starts it, mousemove
    // pans; window mouseup ends it and toggles selection off for a no-move click).
    axis_x
      .style("cursor", "ew-resize")
      .on("mousedown", function(event) {
        if (options.axis_drag_start) options.axis_drag_start(-1, true, event.clientX, event.clientY);
      })
      .on("mousemove", function(event) {
        if (options.axis_hover) options.axis_hover(-1, true);
        if (options.axis_drag_move) options.axis_drag_move(event.clientX, event.clientY);
      })
      .on("mouseenter", function() {
        if (options.axis_hover) options.axis_hover(-1, true);
      })
      .on("mouseleave", function() {
        if (options.axis_unhover) options.axis_unhover();
      });

    // Transparent hit area over the whole bottom strip (date labels + the gaps
    // between them), so hovering targets the X-axis and a drag pans time anywhere
    // there - not just on the line or the date glyphs. Group-local coords:
    // y=0 is the axis line, labels are below. Kept within the plot width and
    // inserted behind the labels so they still receive their own events.
    axis_x.insert("rect", ":first-child")
      .attr("x", options.paddingXLeft)
      .attr("y", 0)
      .attr("width", options.width - options.paddingXLeft - options.paddingXRight)
      .attr("height", options.paddingY)
      .style("fill", "transparent")
      .style("cursor", "ew-resize");

  let anyGrid = false;

  // Map from `${yPos}:${unit}` -> category object — avoids O(N*M) JSON serialize/deserialize per render
  const available_value_categories = new Map();
  for (let s of series) {
    const visible = is_visible(s);
    if (s.grid && visible) anyGrid = true;

    const key = `${s.yPos}:${s.unit}`;
    let cat = available_value_categories.get(key);

    if (cat == null) {
      available_value_categories.set(key, {
        unit: s.unit,
        graph_min: s.graph_min,
        graph_max: s.graph_max,
        override_y_axis_scale: s.override_y_axis_scale,
        override_y_axis_scale_min: s.override_y_axis_scale_min,
        override_y_axis_scale_max: s.override_y_axis_scale_max,
        grid: s.grid,
        zero_line: s.zero_line,
        draw_line_at: s.draw_line_at,
        draw_line_at_value: s.draw_line_at_value,
        visible,
        yLabel: s.yLabel,
        yPos: s.yPos,
        count: 1
      });
    } else {
      if (visible) {
        if (cat.visible) {
          if (s.graph_min < cat.graph_min) cat.graph_min = s.graph_min;
          if (s.graph_max > cat.graph_max) cat.graph_max = s.graph_max;
          if (cat.override_y_axis_scale == false) {
            cat.override_y_axis_scale = s.override_y_axis_scale;
            cat.override_y_axis_scale_min = s.override_y_axis_scale_min;
            cat.override_y_axis_scale_max = s.override_y_axis_scale_max;
          }
          if (cat.grid == false) cat.grid = s.grid;
          if (cat.zero_line == false) cat.zero_line = s.zero_line;
          if (cat.draw_line_at == false) {
            cat.draw_line_at = s.draw_line_at;
            cat.draw_line_at_value = s.draw_line_at_value;
          }
          if (cat.yLabel == "") cat.yLabel = s.yLabel;
          cat.count++;
        } else {
          // First visible series on an axis seeded by hidden ones — reset.
          cat.graph_min = s.graph_min;
          cat.graph_max = s.graph_max;
          cat.override_y_axis_scale = s.override_y_axis_scale;
          cat.override_y_axis_scale_min = s.override_y_axis_scale_min;
          cat.override_y_axis_scale_max = s.override_y_axis_scale_max;
          cat.grid = s.grid;
          cat.zero_line = s.zero_line;
          cat.draw_line_at = s.draw_line_at;
          cat.draw_line_at_value = s.draw_line_at_value;
          cat.yLabel = s.yLabel;
          cat.visible = true;
        }
        cat.override_y_axis_scale = s.override_y_axis_scale;
      }
    }
  }

    // Add x-axis grid
  let bottomGridAxis = d3
  .axisBottom(x)
  .tickFormat('')
  .tickValues(xTickValues)
  .tickSizeInner(-(options.height - options.paddingY * 2));

  let axis_grid_id = "tg_axis_grid";
  let axis_group_grid = canvas.select("#" + axis_grid_id);
  
      // Remove any existing grid
  axis_group_grid.remove(); 

  if (anyGrid) {
    // X-axis grid
    axis_group_grid = canvas.append("g").attr("id", axis_grid_id);  

    axis_group_grid.attr('class', 'tg_axis_grid');
    axis_group_grid.attr("stroke-dasharray", "4,4,4,4,4,4");
    axis_group_grid.attr("transform", "translate(" + `${0},${options.height - options.paddingY}` + ")").call(bottomGridAxis);  
  }

  //let axes = [];
  if (axesData.array != null) {
    for (let initIndex = 0; initIndex < axesData.array.length;initIndex++) {
      let index = axesData.array[initIndex].index;
      let axis_id = "tg_axis_" + index;
      let axis_group = canvas.select("#" + axis_id);
      if (!axis_group.empty()) {
        axis_group.remove();
      }
      let y_axis_value_label_id = "tg_y_axis_value_label_" + index;
      let y_axis_value_label_group = canvas.select("#" + y_axis_value_label_id);
    
          // Remove any existing label
      y_axis_value_label_group.remove();   
      
      let y_axis_label_id = "tg_y_axis_label_" + index;
      let y_axis_label_group = canvas.select("#" + y_axis_label_id);
      
        // Remove any existing label
      y_axis_label_group.remove(); 

      let axis_grid_id = "tg_axis_grid_" + index;
      let axis_group_grid = canvas.select("#" + axis_grid_id);
    
        // Remove any existing grid
      axis_group_grid.remove();  
    }
  }

  axesData.array = [];

  let axisHeight = (options.height - 2*options.paddingY - (graph_N-1)*paddingSplit) / graph_N;
  let index = 0;

  if (axesData.transpose == null) {
    // Init zoom and offset to default
    axesData.transpose = [];
    let initIndex=0;
    for (initIndex=0;initIndex < maxAxis;initIndex++) {
      let transpose = {zoomFactor:1.0, offset:0.0, unit:"", gi:0, baseMin:null, baseMax:null};
      axesData.transpose.push(transpose);
    }
  }

    // Stacked graph index
  for (let row = 0; row < graph_N; row++) {
    const gi = graph_positions[row];
    let rangeMin = options.height - options.paddingY - (row * (axisHeight + paddingSplit));
    let rangeMax = rangeMin - axisHeight;
    let yLegend = rangeMax;

    let right = options.firstYAxisLeft;
    let right_offset = -axesDistance;
    let left_offset = -axesDistance;
    for (let category of available_value_categories.values()) {
      if (category.yPos == gi) {
        const tr = axesData.transpose[index];
        const manualZoom = tr.zoomFactor != 1.0 || tr.offset != 0.0;
        let graphMin, graphMax;
        if (manualZoom && tr.baseMin != null && tr.baseMax != null) {
          graphMin = tr.baseMin;
          graphMax = tr.baseMax;
        } else {
          graphMin = category.override_y_axis_scale ? category.override_y_axis_scale_min : category.graph_min;
          graphMax = category.override_y_axis_scale ? category.override_y_axis_scale_max : category.graph_max;
          if (manualZoom) { tr.baseMin = graphMin; tr.baseMax = graphMax; }
          else { tr.baseMin = null; tr.baseMax = null; }
        }
        let zoom_min = zoomMin(graphMin, graphMax, tr.zoomFactor, tr.offset/axisHeight);
        let zoom_max = zoomMax(graphMin, graphMax, tr.zoomFactor, tr.offset/axisHeight);
          
        if (category.visible) {
          // toggle right
          right = !right;
          if (right)
            right_offset +=axesDistance;
          else
            left_offset +=axesDistance;
        }
              
        axesData.array.push(
          new_value_axis(
            series,
            false, // Disable auto scaling
            category,
            right,
            right ? right_offset : left_offset,
            zoom_min,
            zoom_max,
            rangeMin,
            rangeMax,
            index,
            gi,
            yLegend,
            options
          )
        );
        if (axesData.transpose != null) {
          // Check if existing zoom, offset is valid
          // else reset
          if (axesData.transpose[index].unit != "") {
            if ((axesData.transpose[index].unit != category.unit)
                      ||
                (axesData.transpose[index].gi != gi)) {
              axesData.transpose[index].unit = "";
              axesData.transpose[index].gi = 0;
              axesData.transpose[index].zoomFactor = 1.0;
              axesData.transpose[index].offset = 0.0;
              axesData.transpose[index].baseMin = null;
              axesData.transpose[index].baseMax = null;
            }
          }
        }
        index += 1;
        if (category.visible)
          yLegend += (category.count * 20);
      }
    }
  }

  TG_SERIES_CONTAINER = canvas.select(".tg_series_container");

  let overshoot_ms = (displayed_interval_end - displayed_interval_start) * options.area_overshoot;
  let min_date_to_draw = displayed_interval_start - overshoot_ms;
  let max_date_to_draw = displayed_interval_end + overshoot_ms;

  // Clip date limits to specified interval
  if(min_date_to_draw < options.startDate) min_date_to_draw = options.startDate;
  if(max_date_to_draw > options.endDate) max_date_to_draw = options.endDate;

  xLegendOffset = 0;
  let groupCounts = {};
  axesData.array.forEach(axis => {
    if (axis.visible && axis.rightSide) {
      if (!groupCounts[axis.gi]) {
        groupCounts[axis.gi] = 0;
      }
      groupCounts[axis.gi] += 1;
    }
  });

  let maxCount = 0;
  for (let gi in groupCounts) {
    maxCount = Math.max(maxCount, groupCounts[gi]);
  }

  xLegendOffset = maxCount * axesDistance;

  for (let value_axis of axesData.array) {
    draw_value_axis(
      value_axis,
      canvas,
      options,
      axesData,
      xCoord,
      min_date_to_draw,
      max_date_to_draw,
      visible_ids,
      showHoldValues,
      unique_identifier
    );
  }

  // Rendered once here (not per-axis) so it stays correctly positioned even
  // when there are no series/axes to draw. See draw_diagram_description.
  draw_diagram_description(canvas, options);

  // Set up clip path
   canvas
    .selectAll("#"+unique_identifier+"tg_clipping_rect, .tg_overlay")
    .attr("width", options.width - options.paddingXLeft - options.paddingXRight)
    .attr("height", options.height - options.paddingY*2)
    .attr("x", options.paddingXLeft)
    .attr("y", options.paddingY);

  // Alarm levels
/*   if (alarmLineLow != null && series.length === 1) {
    let alarmLowY = axesData.array[0].getYforPoint({ value: alarmLineLow }) + 1;
    canvas
      .select("#tg_alarm_low_line")
      .style("display", null)
      .attr("stroke-dasharray", "12,12")
      .attr("x1", "0")
      .attr("y1", alarmLowY)
      .attr("x2", options.width)
      .attr("y2", alarmLowY);
  } else {
    canvas.select("#tg_alarm_low_line").style("display", "none");
  }
  if (alarmLineHigh != null && series.length === 1) {
    let alarmHighY = axesData.array[0].getYforPoint({ value: alarmLineHigh }) + 1;
    canvas
      .style("display", null)
      .select("#tg_alarm_high_line")
      .attr("x1", "0")
      .attr("y1", alarmHighY)
      .attr("x2", options.width)
      .attr("y2", alarmHighY);
  } else {
    canvas.select("#tg_alarm_high_line").style("display", "none");
  }
 */
  //axesData.array = axes;

  function zoomMin(category_graph_min,
    category_graph_max, axesZoomFactor, axesOffset) {
      let originalRange = category_graph_max - category_graph_min;
      let newRange = (axesZoomFactor > 0) ? originalRange / axesZoomFactor : originalRange;
      let offset = axesOffset * newRange;
      let newGraphMin = category_graph_min + (originalRange - newRange)/2;
      return newGraphMin + offset;
  }

  function zoomMax(category_graph_min,
    category_graph_max, axesZoomFactor, axesOffset) {
      let originalRange = category_graph_max - category_graph_min;
      let newRange = (axesZoomFactor > 0) ? originalRange / axesZoomFactor : originalRange;
      let offset = axesOffset * newRange;
      let newGraphMax = category_graph_max - (originalRange - newRange)/2;
      return newGraphMax + offset;
  }

  // Bisector created once per render, not on every mousemove
  const bisectDate = d3.bisector(function(d) {
    if (!d) return 0;
    if (d.hasOwnProperty("date")) return d.date;
    return d.minDate;
  }).left;

  function onMouseUp(event) {
    var date_clicked = x.invert(d3.pointer(event)[0]).getTime();
    options.click_callback(date_clicked);
  }

  function onMouseMove(event) {
    const [mouseX, mouseY] = d3.pointer(event);
    onMousePosition(mouseX, mouseY);
  }

  function onMousePosition(mouseX, mouseY) {

  // Magnify rectangle
  let magnify_rect = canvas.select("#tg_magnify_rect");
  if (options.magnify_start_position != null) {
    const last_click_pos = x(options.magnify_start_position);
    if (mouseX < last_click_pos) {
      magnify_rect.attr("x", mouseX).attr("width", last_click_pos - mouseX);
    } else {
      magnify_rect.attr("x", last_click_pos).attr("width", mouseX - last_click_pos);
    }
    magnify_rect
      .attr("visibility", "visible")
      .attr("fill", "rgba(0,0,0,0.1)")
      .attr("y", options.paddingY)
      .attr("height", options.height - options.paddingY * 2);
  } else {
    magnify_rect.attr("visibility", "hidden");
  }

  // Labels / highlight
  const date_at_mouse_pos = x.invert(mouseX).getTime();
  let highlighted_sample_time = date_at_mouse_pos;

  if (!window.app.ui.mousedown &&
      highlighted_sample_time >= options.startDate &&
      highlighted_sample_time <= options.endDate ) {
    let highlighted_sample_position = 0;
    let best_delta = Infinity;
    let highlighted_samples = {};

    // Pass 1: per channel, find the closest real sample to the cursor and
    // record its position. Track the global "winner" — the channel whose
    // closest sample sits nearest the cursor. That winner sets the snap point
    // (highlighted_sample_position / time).
    const channelSamples = [];
    for (let a of axesData.array) {
      for (let series of a.series) {
        if (!series.data || series.data.length === 0) continue;

        series.hidden = !visible_ids.has(series.id);

        const arr = series.data;
        const closestIndex = bisectDate(arr, date_at_mouse_pos, 1);
        const d0 = arr[Math.max(0, closestIndex - 1)];
        const d1 = arr[Math.min(arr.length - 1, closestIndex)];
        // Bucket data points carry `minDate`, not `date` — match the same
        // fallback the bisector accessor and xCoord use. Without this the
        // arithmetic below was `NaN`, the comparison always false, and
        // closest_sample silently snapped to d0 (the left bucket) for every
        // channel — producing the snap that jumps around as the cursor drags.
        const t0 = d0.date ?? d0.minDate;
        const t1 = d1.date ?? d1.minDate;
        let closest_sample =
          date_at_mouse_pos - t0 > t1 - date_at_mouse_pos ? d1 : d0;

        // Skip null gap markers — find nearest real data point instead
        const isNullMarker = d => d && d.minVal === null && d.maxVal === null;
        if (isNullMarker(closest_sample)) {
          let lo = closestIndex - 1, hi = closestIndex;
          while (lo >= 0 && isNullMarker(arr[lo])) lo--;
          while (hi < arr.length && isNullMarker(arr[hi])) hi++;
          const dLo = lo >= 0 ? arr[lo] : null;
          const dHi = hi < arr.length ? arr[hi] : null;
          if (dLo && dHi) {
            const tLo = dLo.minDate?.getTime() ?? 0;
            const tHi = dHi.minDate?.getTime() ?? Infinity;
            closest_sample = Math.abs(date_at_mouse_pos - tLo) <= Math.abs(tHi - date_at_mouse_pos) ? dLo : dHi;
          } else {
            closest_sample = dLo ?? dHi;
          }
          if (!closest_sample) continue;
        }

        if (closest_sample.maxDate < options.startDate ||
            closest_sample.minDate > options.endDate) {
          continue;
        }

        const series_sample_position = xCoord(closest_sample);
        const series_sample_time = x.invert(series_sample_position).getTime();
        const delta = Math.abs(series_sample_time - date_at_mouse_pos);
        if (delta < best_delta) {
          best_delta = delta;
          highlighted_sample_position = series_sample_position;
          highlighted_sample_time = series_sample_time;
        }

        channelSamples.push({ axis: a, series, closest_sample, series_sample_position });
      }
    }

    // Pass 2: render focus circles & collect highlighted_samples ONLY for
    // channels whose closest sample lands at the snap point. A channel whose
    // closest sample is far from the snap (because that channel doesn't have
    // data in this recording, or is in a different recording entirely) is
    // hidden — its circle from the previous frame would otherwise linger and
    // its recording id would pollute the hover label.
    //
    // EXCEPT in live (autoshift) mode: channels stream samples at slightly
    // different timestamps, so as soon as one channel ticks in a new value
    // it becomes the global snap point and every other channel's closest
    // ends up "tooFar" — making every channel except the just-ticked one go
    // blank, flickering between channels with each tick. Disable the gate
    // for autoshift so every channel always shows its own nearest value.
    for (const entry of channelSamples) {
      const { axis: a, series, closest_sample, series_sample_position } = entry;
      const tooFar = !options.autoshift &&
        Math.abs(series_sample_position - highlighted_sample_position) > 1;

      if (options.thick) {
        const yTop = a.getYforPoint({ value: closest_sample.maxVal });
        const yBot = a.getYforPoint({ value: closest_sample.minVal });
        if (Number.isFinite(yTop)) {
          const topOutOfBounds = yTop < a.rangeMax || yTop > a.rangeMin;
          create_focus_circles_category("top", canvas, series, series_sample_position, yTop, tooFar || topOutOfBounds);
        }
        if (Number.isFinite(yBot)) {
          const botOutOfBounds = yBot < a.rangeMax || yBot > a.rangeMin;
          create_focus_circles_category("bottom", canvas, series, series_sample_position, yBot, tooFar || botOutOfBounds);
        }
      } else {
        const y = a.getYforPoint(closest_sample);
        if (Number.isFinite(y)) {
          const outOfBounds = y < a.rangeMax || y > a.rangeMin;
          create_focus_circles_category("bottom", canvas, series, series_sample_position, y, tooFar || outOfBounds);
        }
      }

      if (!series.hidden && !tooFar) {
        highlighted_samples[series.id] = closest_sample;
      }
    }

    let hoverTimeText = "";
    let hoverRecordingText = "";
    if (Number.isFinite(highlighted_sample_position)) {
      hoverTimeText = formatFull(highlighted_sample_time);

      let hoveredRecordingId = null;
      for (const sid in highlighted_samples) {
        const samp = highlighted_samples[sid];
        if (samp && samp.recordingId != null) { hoveredRecordingId = samp.recordingId; break; }
      }
      hoverRecordingText = (typeof options.format_recording_label === "function")
        ? options.format_recording_label(hoveredRecordingId, highlighted_sample_time)
        : "";
    }

    options.highlighted_samples_callback(highlighted_samples, {
      time: hoverTimeText,
      recording: hoverRecordingText
    });

    if (Number.isFinite(highlighted_sample_position)) {
      canvas
        .select("#tg_vertical_line")
        .attr("x1", highlighted_sample_position)
        .attr("x2", highlighted_sample_position)
        .attr("y1", 0)
        .attr("y2", options.height);

      canvas.selectAll(".tg_vertical_line").style("display", null);
      canvas.selectAll(".tg_focus_circles").style("display", null);
    } else {
      canvas.selectAll(".tg_vertical_line").style("display", "none");
      canvas.selectAll(".tg_focus_circles").style("display", "none");
    }
  } else {
    options.highlighted_samples_callback({}, null);
    canvas.selectAll(".tg_vertical_line").style("display", "none");
    canvas.selectAll(".tg_focus_circles").style("display", "none");
  }
}


  function showHoverVisibility() {
    canvas.selectAll(".tg_only_on_hover").style("display", null);
  }

  function hideHoverVisibility() {
    canvas.selectAll(".tg_only_on_hover").style("display", "none");
    options.highlighted_samples_callback({});
    if (options.clear_mouse_position) options.clear_mouse_position();
  }

  canvas
    .select("rect.tg_overlay")
    .on("mouseover", showHoverVisibility)
    .on("mouseout", hideHoverVisibility)
    .on("mousemove", (event) => onMouseMove(event))
    .on("mouseup", (event) => onMouseUp(event));

  // Replay hover at last known mouse position so it updates on re-renders
  // even when the mouse hasn't moved (live data, pan, tick re-renders).
  if (options.last_mouse_client_x != null) {
    const svgNode = canvas.node();
    if (svgNode) {
      const [mx, my] = d3.pointer(
        { clientX: options.last_mouse_client_x, clientY: options.last_mouse_client_y },
        svgNode
      );
      onMousePosition(mx, my);
    }
  }

  return { minDate: displayed_interval_start, maxDate: displayed_interval_end };
}

function draw_value_axis(
  value_axis,
  canvas,
  options,
  axesData,
  getXforPoint,
  lowest_date_to_draw,
  highest_date_to_draw,
  visible_ids,
  showHoldValues,
  unique_identifier
) {
  const is_visible = d => visible_ids.has(d.id);

  let axisVisible = false;

  let yLegendOffset = 0;
  for (let s of value_axis.series) {
    draw_series(
      s,
      getXforPoint,
      canvas,
      options,
      lowest_date_to_draw,
      highest_date_to_draw,
      value_axis,
      !is_visible(s),
      value_axis.yLegend + yLegendOffset,
      unique_identifier
    );

    if (is_visible(s)) {
      yLegendOffset += 20;
      axisVisible = true;
    }
  }

  // --- HOLD LINES (NaN-safe) ---
  const holdable = value_axis.series.filter(s => Number.isFinite(Number(s.hold_value)));
  const hold_lines = canvas
    .selectAll("line.tg_hold_line_" + value_axis.index)
    .data(holdable, d => d.id);

  const hold_value_y = d => value_axis.getYforPoint({ value: d.hold_value });

  hold_lines.enter().append("line")
    .attr("class", "tg_hold_line tg_hold_line_" + value_axis.index)
    .attr("stroke", d => d.color)
    .attr("stroke-dasharray", "3,3");

  hold_lines
    .attr("x1", options.paddingXLeft)
    .attr("y1", d => hold_value_y(d))
    .attr("x2", options.width - options.paddingXRight)
    .attr("y2", d => hold_value_y(d))
    .attr("visibility", d =>
      (!showHoldValues || !is_visible(d)) ? "hidden" : "visible"
    );

  hold_lines.exit().remove();

  // Clear old axis groups if any
  const axis_id = "tg_axis_" + value_axis.index;
  let axis_group = canvas.select("#" + axis_id);
  if (!axis_group.empty()) axis_group.remove();

  const y_axis_value_label_id = "tg_y_axis_value_label_" + value_axis.index;
  let y_axis_value_label_group = canvas.select("#" + y_axis_value_label_id);
  y_axis_value_label_group.remove();

  const axis_grid_id = "tg_axis_grid_" + value_axis.index;
  let axis_group_grid = canvas.select("#" + axis_grid_id);
  axis_group_grid.remove();

  const axis_zero_id = "tg_axis_zero_" + value_axis.index;
  let axis_group_zero = canvas.select("#" + axis_zero_id);
  axis_group_zero.remove();

  const axis_value_line_id = "tg_axis_value_Line_" + value_axis.index;
  let axis_group_value_line = canvas.select("#" + axis_value_line_id);
  axis_group_value_line.remove();

  const y_axis_label_id = "tg_y_axis_label_" + value_axis.index;
  let y_axis_label_group = canvas.select("#" + y_axis_label_id);
  y_axis_label_group.remove();

  let axis = null;
  let axisGrid = null;
  let axisOffset = null;

  if (value_axis.visible) {
    if (value_axis.rightSide) {
      axis = d3.axisRight(value_axis.yFunction);
      if (value_axis.grid) {
        axisOffset = value_axis.offset;
        axisGrid = d3.axisRight(value_axis.yFunction);
        axisGrid
          .tickFormat('')
          .ticks((value_axis.rangeMin - value_axis.rangeMax) / 30)
          .tickSizeInner(-(options.width - options.paddingXLeft - options.paddingXRight + axisOffset));
      }
    } else {
      axis = d3.axisLeft(value_axis.yFunction);
      if (value_axis.grid) {
        axisOffset = value_axis.offset;
        axisGrid = d3.axisLeft(value_axis.yFunction);
        axisGrid
          .tickFormat('')
          .ticks((value_axis.rangeMin - value_axis.rangeMax) / 30)
          .tickSizeInner(-(options.width - options.paddingXLeft - options.paddingXRight + axisOffset));
      }
    }

    const tick_count = (value_axis.rangeMin - value_axis.rangeMax) / 30;
    axis.ticks(tick_count);

    // d3's default format for a value axis is ",f": thousands grouped with a comma and
    // a dot for the decimal mark. The rest of the interface writes thousands without a
    // separator and takes the decimal mark from the user's settings. Asking d3 for "f"
    // keeps its own choice of precision per tick but drops the grouping, and the mark
    // is swapped afterwards. Read inside the callback so a changed setting applies
    // without rebuilding the axis.
    const d3_tick_format = typeof value_axis.yFunction.tickFormat === "function"
      ? value_axis.yFunction.tickFormat(tick_count, "f")
      : String;

    axis.tickFormat(value => {
      const text = d3_tick_format(value);
      const decimal_point = DataStore.settings.decimal_point;
      return decimal_point === "." ? text : text.replace(".", decimal_point);
    });

    axis_group = canvas.append("g").attr("id", axis_id);

    axis_group.append("title")
      .text("Click to activate scroll functionality for this axis");

    axis_group
      .attr("visibility", axisVisible ? "visible" : "hidden")
      .attr("transform", `translate(${value_axis.x},${0})`)
      .call(axis);

    // Drag directly on the axis to pan/zoom it. mousedown selects this axis and
    // starts the pan; mousemove (button held) pans it; window mouseup ends it and
    // toggles selection off for a no-move click. (Replaces the old click-to-select
    // + drag-in-the-plot flow and the stubbed onMouse*YAxis handlers.)
    axis_group.on("mousedown", function(event) {
      if (options.axis_drag_start) options.axis_drag_start(value_axis.index, false, event.clientX, event.clientY);
    });
    axis_group.on("mousemove", function(event) {
      // Keep this axis targeted while the cursor is over it (also re-arms after a
      // drag), then continue any active pan.
      if (options.axis_hover) options.axis_hover(value_axis.index, false);
      if (options.axis_drag_move) options.axis_drag_move(event.clientX, event.clientY);
    });
    // Hovering an axis targets it for scroll-zoom (no click/selection needed).
    axis_group.on("mouseenter", function() {
      if (options.axis_hover) options.axis_hover(value_axis.index, false);
    });
    axis_group.on("mouseleave", function() {
      if (options.axis_unhover) options.axis_unhover();
    });
    axis_group.style("cursor", "ns-resize");

    if (value_axis.grid) {
      axis_group_grid = canvas.append("g").attr("id", axis_grid_id);
      axis_group_grid
        .attr('class', 'tg_axis_grid')
        .attr("stroke-dasharray", "4,4,4,4,4,4")
        .attr("transform", `translate(${value_axis.x},${0})`)
        .call(axisGrid);
    }

    // Axes are no longer "selected" (no green highlight) — they're driven by
    // hover (cursor) + drag/scroll directly.
    axis_group.style('fill', 'white').call(axis)
      .attr("font-size", AXIS_TICK_FONT_SIZE);

    // Transparent hit area covering the whole axis strip (the numbers AND the
    // gaps between them), so hovering targets the axis for scroll-zoom and a drag
    // pans it anywhere on the strip — not just on the line or the number glyphs.
    // Inserted behind the ticks/labels and in group-local coords (x=0 is the axis
    // line). Triggers the same group handlers above.
    const Y_HIT_W = 55;
    axis_group.insert("rect", ":first-child")
      .attr("x", value_axis.rightSide ? 0 : -Y_HIT_W)
      .attr("y", value_axis.rangeMax)
      .attr("width", Y_HIT_W)
      .attr("height", value_axis.rangeMin - value_axis.rangeMax)
      .style("fill", "transparent")
      .style("cursor", "ns-resize");

    // Value label near axis
    let axisLabelX;
    const labelOffset = 5;
    axisLabelX = value_axis.rightSide ? (value_axis.x + labelOffset) : (value_axis.x - labelOffset);

    let labelGroup = canvas.append("g")
      .attr("id", y_axis_value_label_id)
      .style("cursor", "pointer");

    // Unit label sits ABOVE the top of its axis (conventional). rangeMax is the
    // top edge of this (sub-)graph, so place the label just above it.
    let unitLabelY = value_axis.rangeMax - 10;
    let textElement = labelGroup.append("text")
      .attr("x", axisLabelX)
      .attr("y", unitLabelY)
      .text(value_axis.name)
      .style("fill", "black")
      .style("font-size", AXIS_UNIT_FONT_SIZE)
      .style("stroke", "none");

    textElement.append("title").text("Click to configure axis settings");

    let bbox = measureText(textElement.node());
    // Keep the unit label inside the SVG: the left one would otherwise hang off
    // the left edge and the right-axis one off the right edge.
    const EDGE = 3;
    if (bbox.x < EDGE) {
      textElement.attr("x", axisLabelX + (EDGE - bbox.x));
      bbox = measureText(textElement.node());
    } else if (bbox.x + bbox.width > options.width - EDGE) {
      textElement.attr("x", axisLabelX - (bbox.x + bbox.width - (options.width - EDGE)));
      bbox = measureText(textElement.node());
    }
    // Keep it from clipping off the top of the SVG (top-most stacked graph).
    if (bbox.y < EDGE) {
      textElement.attr("y", unitLabelY + (EDGE - bbox.y));
      bbox = measureText(textElement.node());
    }
    labelGroup.insert("rect", "text")
      .attr("x", bbox.x - 5)
      .attr("y", bbox.y - 2)
      .attr("width", bbox.width + 10)
      .attr("height", bbox.height + 4)
      .style("fill", "white")
      .style("stroke", "none");

    labelGroup.on("click", function() { onClickYAxisLabel(canvas, options, value_axis); })
      .on("mouseover", function() {
        textElement.style("fill", "white");
        labelGroup.select("rect").style("fill", "#007bff");
      })
      .on("mouseout", function() {
        textElement.style("fill", "black");
        labelGroup.select("rect").style("fill", "white");
      });

    const axisLabelY = (value_axis.rangeMin + value_axis.rangeMax) / 2;

    if (value_axis.yLabel != "") {
      // Place the rotated title just beyond the tick numbers instead of at a
      // fixed offset: measure the widest tick label on this axis (numbers vary a
      // lot — 0.0008, 2,500, -1,000…) so a constant offset can't clear them all.
      let maxTickWidth = 0;
      axis_group.selectAll(".tick text").each(function () {
        const bb = this.getBBox ? this.getBBox() : null;
        if (bb && bb.width > maxTickWidth) maxTickWidth = bb.width;
      });
      // Widest number + tick gap (~9) + half the rotated title's height (~8) + gap.
      const clearance = maxTickWidth + 18;
      let offset = value_axis.rightSide ? clearance : -clearance;
      // Never let a left-side title slide off the SVG's left edge — clamp it to a
      // small margin (it can then sit closer to the numbers, but stays visible).
      if (!value_axis.rightSide && (axisLabelX + offset) < 10) {
        offset = 10 - axisLabelX;
      }

      let yLabelGroup = canvas.append("g")
        .attr("id", y_axis_label_id)
        .attr("transform", `translate(${axisLabelX + offset}, ${axisLabelY}) rotate(-90)`)
        .style("cursor", "pointer");

      let yLabelText = yLabelGroup.append("text")
        .attr("text-anchor", "middle")
        .text(value_axis.yLabel)
        .style("fill", "black")
        .style("font-size", AXIS_TITLE_FONT_SIZE);

      let bbox2 = measureText(yLabelText.node());

      let yLabelBg = yLabelGroup.insert("rect", "text")
        .attr("x", bbox2.x - 5)
        .attr("y", bbox2.y - 2)
        .attr("width", bbox2.width + 10)
        .attr("height", bbox2.height + 4)
        .style("fill", "white")
        .style("stroke", "none");

      yLabelGroup.on("click", function() { onClickYAxisLabel(canvas, options, value_axis); })
        .on("mouseover", function() {
          yLabelText.style("fill", "white");
          yLabelBg.style("fill", "#007bff");
        })
        .on("mouseout", function() {
          yLabelText.style("fill", "black");
          yLabelBg.style("fill", "white");
        });
    }

    // --- ZERO LINE (NaN-safe) ---
    if (value_axis.zero_line) {
      const y0 = value_axis.getYforPoint({ value: 0 });
      if (Number.isFinite(y0)) {
        axis_group_zero = canvas.append("g").attr("id", axis_zero_id);
        axis_group_zero
          .append("line")
          .attr("stroke", "lightgray")
          .attr("stroke-width", "1px")
          .attr("stroke-dasharray", "20,10,5,5,5,10")
          .attr("x1", options.paddingXLeft)
          .attr("y1", y0 + 1)
          .attr("x2", options.width - options.paddingXRight)
          .attr("y2", y0 + 1);
      }
    }

    // --- DRAW-LINE-AT (NaN-safe) ---
    if (value_axis.draw_line_at) {
      const yv = value_axis.getYforPoint({ value: value_axis.draw_line_at_value });
      if (Number.isFinite(yv)) {
        axis_group_value_line = canvas.append("g").attr("id", axis_value_line_id);
        axis_group_value_line
          .append("line")
          .attr("stroke", "red")
          .attr("stroke-dasharray", "20,10,5,5,5,10")
          .attr("x1", options.paddingXLeft)
          .attr("y1", yv + 1)
          .attr("x2", options.width - options.paddingXRight)
          .attr("y2", yv + 1);
      }
    }

    axesData.array[value_axis.index].yAxis = axis;
  }
}

// Diagram description label (e.g. the zone name), pinned to the bottom-right of
// the plot. Rendered from render_graph directly (NOT per-axis) so it always
// appears at the correct position even when there is no data to plot — with no
// series there are no axes, draw_value_axis never runs, and this would
// otherwise be left as a stale element floating in the graph.
function draw_diagram_description(canvas, options)
{
  const diagram_description_id = "tg_diagram_description";
  canvas.select("#" + diagram_description_id).remove();

  const descriptionX = options.width - options.paddingXRight - xLegendOffset;
  const descriptionY = options.height - 5;
  const diagramDescription = options.graph_description;

  if (diagramDescription != "") {
    const group = canvas.append("g")
      .attr("id", diagram_description_id)
      .attr("transform", `translate(${descriptionX}, ${descriptionY})`)
      .style("cursor", "pointer");

    const textElement = group.append("text")
      .attr("text-anchor", "end")
      .text(diagramDescription)
      .style("fill", "black")
      .style("font-size", "14px");

    textElement.append("title").text("Click to change graph description");

    const bbox = measureText(textElement.node());

    const rectElement = group.insert("rect", "text")
      .attr("x", bbox.x - 5)
      .attr("y", bbox.y - 2)
      .attr("width", bbox.width + 10)
      .attr("height", bbox.height + 4)
      .style("fill", "white")
      .style("stroke", "none");

    group.on("click", function() { onClickGraphDescription(options); })
      .on("mouseover", function() {
        textElement.style("fill", "white");
        rectElement.style("fill", "#007bff");
      })
      .on("mouseout", function() {
        textElement.style("fill", "black");
        rectElement.style("fill", "white");
      });
  }
}

function onClickYAxisLabel(canvas, options, value_axis)
{
  // console.log("Clicked:"+ value_axis.name + ", index:"+ value_axis.index);
  options.axis_options_callback(value_axis.index, value_axis.name, 
    value_axis.override_y_axis_scale,
    value_axis.override_y_axis_scale_min,
    value_axis.override_y_axis_scale_max,
    value_axis.grid, value_axis.zero_line, value_axis.draw_line_at, value_axis.draw_line_at_value, value_axis.yLabel);
}

function onClickGraphDescription(options)
{
  // console.log("Clicked graph description");
  options.graph_description_callback();
}

function draw_series(
  s,
  xCoord,
  canvas,
  options,
  min_tstamp,
  max_tstamp,
  value_axis,
  hidden,
  yLegend,
  unique_identifier
) 
{
  let series_id = "series_" + s.id;
  let series_group = TG_SERIES_CONTAINER.select("#" + series_id);
  if (series_group.empty()) {
    series_group = TG_SERIES_CONTAINER.append("g").attr("id", series_id);
  }  
  
  // Path
  let path;
  if (value_axis.gi == 0) {
    path = series_group.select("path.tg_path");
    if (path.empty()) {
      path = series_group.append("path").attr("class", "tg_path");
    }
  }
  else if (value_axis.gi == 1){
    path = series_group.select("path.tg_path_1");
    if (path.empty()) {
      path = series_group.append("path").attr("class", "tg_path_1");
    }      
  }
  else {
    path = series_group.select("path.tg_path_2");
    if (path.empty()) {
      path = series_group.append("path").attr("class", "tg_path_2");
    }      
  }
  path.attr("visibility", hidden ? "hidden" : "visible");

  // A series is drawn on exactly one axis (its unit + yPos), so line and bar
  // paths of other positions are leftovers from before the series was moved to
  // another stacked graph — remove them or the old line stays on screen.
  const ownPathClasses = [path.attr("class"), `tg_minmax_bars_${value_axis.gi}`];
  series_group.selectAll("path")
    .filter(function () { return !ownPathClasses.includes(this.getAttribute("class")); })
    .remove();

  let legend_id = "legend_" + s.id;
  let legend_group = canvas.select("#" + legend_id);
  legend_group.remove();

  let legend_label_id = "legend_label_" + s.id;
  let legend_label_group = canvas.select("#" + legend_label_id);
  legend_label_group.remove();

  if (hidden) {
    // Aggressive cleanup: actually remove the path nodes rather than clearing d.
    // The select-or-create pattern below in the visible branch will recreate
    // them when the channel is shown again. This is the safest approach to
    // ensure no stale data is rendered, in case d="" isn't taking effect for
    // some reason (e.g. browser caching, attribute not propagating, etc.).
    series_group.selectAll("path").remove();
    series_group.selectAll("line.tg_minmax_bar").remove();
    return;
  }

    // Draw printable legend
  if (options.show_legend) {
    // console.log(s.name + " at " + yLegend);
    legend_group = canvas.append("g").attr("id", legend_id);
    let xLegend = options.width - options.paddingXRight + xLegendDistance + xLegendOffset;
    legend_group.append("circle")
                  .attr("cx", xLegend)
                  .attr("cy", yLegend)
                  .attr("r", 8)
                  .style("fill",s.color);
                  
                  /* var dragHandler = d3.drag()
                      .on("drag", function () {
                        d3.select(this)
                        .attr("cx", d3.event.x)
                        .attr("cy", d3.event.y);                        

                          console.log("X:"+d3.event.x+" Y:" + d3.event.y);

                      });
                  
                  dragHandler(canvas.selectAll("circle")); */
  
    legend_label_group = canvas.append("g").attr("id", legend_label_id);
    legend_label_group.append("text")
      .attr('class', 'legend_label')
      .attr("x", xLegend + 10)
      .attr("y", yLegend)
      .style("fill", s.color)
      .text(s.name)
      .attr("text-anchor", "left")
      .style("alignment-baseline", "middle");
  }

  if (value_axis.gi == 0) {
    let clipId = unique_identifier + "clip-0"

    canvas.append("clipPath")
    .attr("class","clipPath")
    .attr("id", clipId)  // <-- we need to use the ID of clipPath
    .append("rect")
    .attr("x", options.paddingXLeft)
    .attr("y", value_axis.rangeMax)
    .attr("width", options.width)
    .attr("height", value_axis.rangeMin - value_axis.rangeMax);
    path.attr("clip-path", `url(#${clipId})`);
  }
  else if (value_axis.gi == 1) {
    let clipId = unique_identifier + "clip-1"
    canvas.append("clipPath")
    .attr("id", clipId)  // <-- we need to use the ID of clipPath
    .attr("class","clipPath")
    .append("rect")
    .attr("x", options.paddingXLeft)
    .attr("y", value_axis.rangeMax)
    .attr("width", options.width)
    .attr("height", value_axis.rangeMin - value_axis.rangeMax);  
    path.attr("clip-path", `url(#${clipId})`);  
  }
  else {
    let clipId = unique_identifier + "clip-2"
    canvas.append("clipPath")
    .attr("id", clipId)  // <-- we need to use the ID of clipPath
    .attr("class","clipPath")
    .append("rect")
    .attr("x", options.paddingXLeft)
    .attr("y", value_axis.rangeMax)
    .attr("width", options.width)
    .attr("height", value_axis.rangeMin - value_axis.rangeMax);
    path.attr("clip-path", `url(#${clipId})`);
  }

  const lineFunction = d3
    .line()
    .curve(d3.curveLinear)
    .x(xCoord)
    .y(value_axis.getYforPoint)
    .defined(d => d.value != null && isFiniteNum(d.value) && isFiniteNum(xCoord(d)));

  const effectiveThick = false;

  let data = get_datum_array_from_series_data(
    s.data,
    effectiveThick,
    min_tstamp,
    max_tstamp,
    options.recordings
  );

  if (s.graph_type == Enum.graph_type.graph_type_line)
  {
    path
      .datum(data)
      .attr("fill", effectiveThick ? s.color : "none")
      .attr("stroke", s.color)
      .attr("d", lineFunction);

    const clipId = unique_identifier + "clip-" + value_axis.gi;
    if (!effectiveThick && options.thick) {
      // Bisect to find the visible window in O(log N), then walk only that
      // window. Previously this was a full s.data.filter() scan per series.
      const firstIdx = bisectLeft(s.data, min_tstamp);
      const lastIdx = bisectRight(s.data, max_tstamp);
      const visibleSpan = lastIdx - firstIdx;
      // When point density exceeds ~1 per pixel the bars overlap and become a
      // solid block — drawing each one wastes DOM and dominates render time at
      // coarse zoom. Stride through the window so we emit at most ~graphWidth
      // bars. For each stride group we emit a synthetic bar that spans the
      // union of all bucket min/max values inside the group, so the visible
      // envelope honestly reflects the full data range in that pixel column
      // (no extremes hidden between adjacent buckets).
      const graphWidth = options.width - options.paddingXLeft - options.paddingXRight;
      const stride = visibleSpan > 0 ? Math.max(1, Math.floor(visibleSpan / Math.max(1, graphWidth))) : 1;
      const rawSlice = [];
      if (stride === 1) {
        for (let i = firstIdx; i < lastIdx; i++) {
          const d = s.data[i];
          if (d && toNum(d.minVal) != null && toNum(d.maxVal) != null) rawSlice.push(d);
        }
      } else {
        for (let bucketStart = firstIdx; bucketStart < lastIdx; bucketStart += stride) {
          const bucketEnd = Math.min(bucketStart + stride, lastIdx);
          let unionMin = Infinity, unionMax = -Infinity;
          let firstReal = null;
          for (let i = bucketStart; i < bucketEnd; i++) {
            const d = s.data[i];
            if (!d) continue;
            const lo = toNum(d.minVal), hi = toNum(d.maxVal);
            if (lo == null || hi == null) continue;
            if (firstReal == null) firstReal = d;
            if (lo < unionMin) unionMin = lo;
            if (hi > unionMax) unionMax = hi;
          }
          if (firstReal && Number.isFinite(unionMin) && Number.isFinite(unionMax)) {
            // Synthetic bucket: position taken from the first real bucket in the
            // group (so bar spacing follows actual data points); range is the
            // union of all bucket ranges in the group.
            rawSlice.push({
              minDate: firstReal.minDate,
              maxDate: firstReal.maxDate,
              minVal: unionMin,
              maxVal: unionMax,
              avgVal: firstReal.avgVal
            });
          }
        }
      }
      // Collapse all min-max bars for this series into ONE <path> using
      // alternating M/L commands ("move-to start of bar, line-to end of bar").
      // Visually identical to N <line> elements but ~1000× fewer DOM nodes —
      // SVG renders a multi-segment path far faster than N elements that each
      // need their own layout slot. On a 17-channel dense diagram this drops
      // the bar DOM count from ~17000 to 17.
      let pathStr = "";
      for (let i = 0; i < rawSlice.length; i++) {
        const d = rawSlice[i];
        const x = xCoord(d);
        const yMin = value_axis.getYforPoint({ value: d.minVal });
        const yMax = value_axis.getYforPoint({ value: d.maxVal });
        // Skip if any coord is non-finite — would corrupt the path string
        if (!isFiniteNum(x) || !isFiniteNum(yMin) || !isFiniteNum(yMax)) continue;
        pathStr += `M${x} ${yMin}L${x} ${yMax}`;
      }
      // Axis-specific class so a series rendered across multiple y-axes keeps
      // separate bar paths (mirrors the line-path naming: tg_path / tg_path_1 / tg_path_2).
      const barClass = `tg_minmax_bars_${value_axis.gi}`;
      let barPath = series_group.select(`path.${barClass}`);
      if (barPath.empty()) {
        barPath = series_group.append("path").attr("class", barClass);
      }
      barPath
        .attr("clip-path", `url(#${clipId})`)
        .attr("stroke", s.color)
        .attr("stroke-width", 1.5)
        .attr("fill", "none")
        .attr("d", pathStr);
      // One-time migration: remove any old <line.tg_minmax_bar> elements left
      // over from the prior implementation (cheap no-op once cleaned up).
      series_group.selectAll("line.tg_minmax_bar").remove();
    } else {
      // Non-thick: clear any stale bar paths (all axis variants) and old lines.
      series_group.selectAll("[class^='tg_minmax_bars']").attr("d", "");
      series_group.selectAll("line.tg_minmax_bar").remove();
    }
  }


  if (s.graph_type == Enum.graph_type.graph_type_bar) {
    const clean = data.filter(d => d.value != null && isFiniteNum(d.value) && isFiniteNum(xCoord(d)));
    canvas.append('g').attr('fill', s.color)
      .selectAll('rect').data(clean).join('rect')
      .attr('x', xCoord)
      .attr('y', value_axis.getYforPoint)
      .attr('height', d => value_axis.rangeMin - value_axis.yFunction(d.value))
      .attr('width', 10)
      .attr('class', 'barrect');
  }

  window.points_drawn += data.length;


  /*
  // Points
  let number_of_points_before_hide = options.width / options.maxPointDensity;
  let point_visibility = (hidden || options.thick || s.data.length >= number_of_points_before_hide) ? "hidden" : "visible";
  let _points = series_group.selectAll("circle.tg_point").data(data);

  _points.exit().remove();

  _points
    .enter()
    .append("circle")
    .attr("class", "tg_point")
    .attr("fill", s.color)
    .attr("r", 2)
    .attr("cx", xCoord)
    .attr("visibility", point_visibility)
    .attr("cy", value_axis.getYforPoint);

  if(point_visibility) {
    _points
      .attr("cx", xCoord)
      //.attr("visibility", point_visibility)
      .attr("cy", value_axis.getYforPoint);
  }
  */
}

function create_focus_circles_category(
  category_name,
  canvas,
  series,
  mouse_x_pos,
  y_func,
  forceHide = false
)
{
  let focus_circles_group = canvas.select(".tg_focus_circles");
  let matching_id = `focus_circle_${category_name}_${series.id}`;

  let matching_circles = focus_circles_group.select("#" + matching_id);

  if (matching_circles.empty()) {
    focus_circles_group
      .append("circle")
      .attr("r", "3")
      .attr("id", matching_id);

    matching_circles = focus_circles_group.select("#" + matching_id);
  }

  matching_circles
    .attr("cx", mouse_x_pos)
    .attr("cy", y_func)
    .attr("display", (series.hidden || forceHide) ? "none" : null)
    .attr("fill", series.color);
}

function new_value_axis(
  series,
  autoscale_axis,
  category,
  rightSide,
  offset,
  zoom_min,
  zoom_max,
  rangeMin,
  rangeMax,
  index,
  gi,
  yLegend,
  options
) {
  // local helpers (self-contained)
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const finiteExtent = (arr) => {
    const vals = arr.map(Number).filter(Number.isFinite);
    if (!vals.length) return [0, 1];
    let min = Math.min(...vals), max = Math.max(...vals);
    if (min === max) {
      const pad = Math.max(1e-6, Math.abs(min || 1) * 0.01);
      min -= pad; max += pad;
    }
    return [min, max];
  };

  const axisSeries = (series || []).filter(
    s => s.unit === category.unit && s.yPos == gi
  );

  const collectDataExtent = () => {
    let min = Infinity, max = -Infinity;
    for (const ss of axisSeries) {
      const ext = getDataExtent(ss.data || []);
      if (ext) {
        if (ext[0] < min) min = ext[0];
        if (ext[1] > max) max = ext[1];
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
    if (min === max) {
      const pad = Math.max(1e-6, Math.abs(min || 1) * 0.01);
      return [min - pad, max + pad];
    }
    return [min, max];
  };

  let [minY, maxY] = autoscale_axis ? collectDataExtent() : finiteExtent([zoom_min, zoom_max]);

  const rangePad = (maxY - minY) * Y_AXIS_PAD_FRACTION;
  minY -= rangePad;
  maxY += rangePad;

  // assemble axis object
  const axis = {
    name: category.unit,
    rightSide,
    visible: category.visible,
    offset,
    override_y_axis_scale: category.override_y_axis_scale,
    override_y_axis_scale_min: category.override_y_axis_scale_min,
    override_y_axis_scale_max: category.override_y_axis_scale_max,
    grid: category.grid,
    zero_line: category.zero_line,
    draw_line_at: category.draw_line_at,
    draw_line_at_value: category.draw_line_at_value,
    yLabel: category.yLabel,
    index,
    gi,                // stacked graph index
    yLegend,
    rangeMin,
    rangeMax,
    unit: category.unit,
    series: axisSeries,
    minY,
    maxY,
    yFunction: null,
    getYforPoint: null,
    x: 0,
    yAxis: null       // filled later by caller
  };

  axis.yFunction = d3
    .scaleLinear()
    .domain([axis.minY, axis.maxY])
    .rangeRound([rangeMin, rangeMax]);

  axis.getYforPoint = (d) => {
    const raw = d
      ? (d.value ?? ((d.minVal != null || d.maxVal != null)
          ? (Number(d.minVal) + Number(d.maxVal)) / 2
          : null))
      : null;
    const n = Number(raw);
    return Number.isFinite(n) ? axis.yFunction(n) : NaN;
  };

  if (axis.rightSide) {
    axis.x = options.width - options.paddingXRight + axis.offset;
  } else {
    axis.x = options.paddingXLeft - axis.offset;
  }

  return axis;
}


function get_datum_array_from_series_data(series_data, thick, min_date, max_date, recordings) {
  const first_index = Math.max(0, bisectLeft(series_data, min_date) - 1);
  if (first_index >= series_data.length) return [];
  const last_index = Math.min(series_data.length, bisectRight(series_data, max_date) + 1);

  const slice = series_data.slice(first_index, last_index)
    .filter(d => d != null);

  if (!thick) {
    return slice.map(d => {
      let value;
      if (d.value != null) {
        value = toNum(d.value);
      } else if (d.avgVal != null) {
        value = toNum(d.avgVal);
      } else if (d.minVal != null && d.maxVal != null) {
        value = (toNum(d.minVal) + toNum(d.maxVal)) / 2;
      } else {
        value = toNum(d.minVal ?? d.maxVal);
      }
      return { date: d.date ?? d.minDate, minDate: d.minDate ?? d.date, value };
    });
  }

  // thick: build top/bottom polygon per contiguous segment, null sentinels break path at gaps
  const sentinel = { date: null, minDate: null, value: null };
  const out = [];
  const addSegment = (seg) => {
    if (!seg.length) return;
    for (const d of seg) {
      const date = d.date ?? d.minDate;
      out.push({ date, minDate: date, value: toNum(d.maxVal) });
    }
    for (let i = seg.length - 1; i >= 0; i--) {
      const d = seg[i];
      const date = d.date ?? d.minDate;
      out.push({ date, minDate: date, value: toNum(d.minVal) });
    }
    out.push(sentinel);
  };
  let seg = [];
  for (const d of slice) {
    const date = d.date ?? d.minDate;
    const vMax = toNum(d.maxVal);
    const vMin = toNum(d.minVal);
    if (!isFiniteNum(+date) || vMax == null || vMin == null) {
      addSegment(seg);
      seg = [];
    } else {
      seg.push(d);
    }
  }
  addSegment(seg);
  return out;
}

function assert_order_of_data(data) {
  let last_date = 0;
  for (let i = 0; i < data.length; i++) {
    let start_date = data[i].date;
    if (start_date < last_date) {
      // console.log(
      //   `Samples not in order! \nLast: ${last_date}, \n\nThis: ${start_date}`
      // );
      window.bad_data = data;
      return false;
    }
    last_date = start_date;
  }
  return true;
}
